import React, { useEffect, useState } from "react";
import { Users, Mail, Ticket, TrendingUp, CalendarDays, UserPlus, Eye, Clock, MapPin, Monitor, Globe } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, BarChart, Bar } from "recharts";
import api from "../../lib/api";

const RANGES = [{ k: "7d", l: "7 days" }, { k: "30d", l: "30 days" }, { k: "90d", l: "90 days" }, { k: "all", l: "All time" }];
const PIE_COLORS = ["#1C3A27", "#C86A4C", "#E5CAA4", "#6B8F71", "#9C4A2E", "#C9A24B"];
const fmtTime = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

export default function AdminAnalytics() {
  const [tab, setTab] = useState("membership");
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setError(false);
    const url = tab === "traffic" ? "/admin/analytics/traffic" : "/admin/analytics";
    api.get(`${url}?range=${range}`).then((r) => (tab === "traffic" ? setTraffic(r.data) : setData(r.data))).catch(() => setError(true));
  }, [range, tab, tick]);

  const t = data?.totals || {};
  const membershipCards = [
    { label: "New members", value: t.new_members, Icon: UserPlus },
    { label: "Event RSVPs", value: t.rsvps, Icon: Ticket },
    { label: "Contact messages", value: t.contacts, Icon: Mail },
    { label: "Total members", value: t.total_members, Icon: Users },
  ];
  const tt = traffic?.totals || {};
  const trafficCards = [
    { label: "Page views", value: tt.views, Icon: Eye },
    { label: "Unique visitors", value: tt.visitors, Icon: Users },
    { label: "Sessions", value: tt.sessions, Icon: Globe },
    { label: "Avg. time on page", value: tt.avg_dwell != null ? fmtTime(tt.avg_dwell) : "—", Icon: Clock },
  ];
  const cards = tab === "traffic" ? trafficCards : membershipCards;

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-medium text-primary">Analytics & Overview</h1>
          <p className="text-muted-foreground text-sm mt-1">Live metrics — updates instantly, no redeploy needed.</p>
        </div>
        <div className="flex rounded-full border border-primary/20 overflow-hidden">
          {RANGES.map((r) => (
            <button key={r.k} data-testid={`range-${r.k}`} onClick={() => setRange(r.k)}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${range === r.k ? "bg-primary text-primary-foreground" : "text-primary hover:bg-primary/5"}`}>{r.l}</button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {[{ k: "membership", l: "Membership" }, { k: "traffic", l: "Website traffic" }].map((x) => (
          <button key={x.k} data-testid={`analytics-tab-${x.k}`} onClick={() => setTab(x.k)}
            className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === x.k ? "bg-secondary text-secondary-foreground" : "border border-primary/20 text-primary hover:bg-primary/5"}`}>{x.l}</button>
        ))}
      </div>

      {error && (
        <div data-testid="analytics-error" className="bg-destructive/10 text-destructive rounded-2xl p-4 mb-4 flex items-center justify-between">
          <span className="text-sm font-medium">Couldn't load analytics.</span>
          <button onClick={() => setTick((n) => n + 1)} className="text-sm font-semibold underline">Retry</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-card border border-primary/10 rounded-2xl p-5">
            <div className="w-10 h-10 rounded-full bg-secondary/15 text-secondary flex items-center justify-center mb-3"><c.Icon size={18} /></div>
            <p className="font-display text-4xl font-semibold text-primary">{c.value ?? "—"}</p>
            <p className="text-sm text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>

      {tab === "membership" ? (
        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 bg-card border border-primary/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4"><TrendingUp size={18} className="text-secondary" /><h2 className="font-display text-xl font-semibold text-primary">Growth over time</h2></div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={data?.series || []} margin={{ left: -20, right: 10 }}>
                <defs>
                  <linearGradient id="gm" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1C3A27" stopOpacity={0.4} /><stop offset="100%" stopColor="#1C3A27" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C86A4C" stopOpacity={0.4} /><stop offset="100%" stopColor="#C86A4C" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d5" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6b7a70" }} interval="preserveStartEnd" minTickGap={30} />
                <YAxis tick={{ fontSize: 11, fill: "#6b7a70" }} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="members" stroke="#1C3A27" strokeWidth={2} fill="url(#gm)" name="New members" />
                <Area type="monotone" dataKey="rsvps" stroke="#C86A4C" strokeWidth={2} fill="url(#gr)" name="RSVPs" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-card border border-primary/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4"><Users size={18} className="text-secondary" /><h2 className="font-display text-xl font-semibold text-primary">Members by tier</h2></div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data?.tier_breakdown || []} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {(data?.tier_breakdown || []).map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 mt-2">
              {(data?.tier_breakdown || []).map((e, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-foreground/80"><span className="w-3 h-3 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /> {e.name}</span>
                  <span className="font-semibold text-primary">{e.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid lg:grid-cols-3 gap-6 mt-6">
            <div className="lg:col-span-2 bg-card border border-primary/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><Eye size={18} className="text-secondary" /><h2 className="font-display text-xl font-semibold text-primary">Page views over time</h2></div>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={traffic?.series || []} margin={{ left: -20, right: 10 }}>
                  <defs><linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C86A4C" stopOpacity={0.45} /><stop offset="100%" stopColor="#C86A4C" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d5" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6b7a70" }} interval="preserveStartEnd" minTickGap={30} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b7a70" }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="views" stroke="#C86A4C" strokeWidth={2} fill="url(#gv)" name="Views" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-card border border-primary/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><Monitor size={18} className="text-secondary" /><h2 className="font-display text-xl font-semibold text-primary">Devices</h2></div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={traffic?.devices || []} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3}>
                    {(traffic?.devices || []).map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {(traffic?.devices || []).map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-sm"><span className="flex items-center gap-2 text-foreground/80"><span className="w-3 h-3 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /> {e.name}</span><span className="font-semibold text-primary">{e.value}</span></div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mt-6">
            <div className="bg-card border border-primary/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><Globe size={18} className="text-secondary" /><h2 className="font-display text-lg font-semibold text-primary">Traffic sources</h2></div>
              {(traffic?.sources || []).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-primary/5"><span className="text-foreground/80">{s.name}</span><span className="font-semibold text-primary">{s.value}</span></div>
              ))}
              {!(traffic?.sources || []).length && <p className="text-sm text-muted-foreground">No data yet.</p>}
            </div>
            <div className="bg-card border border-primary/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><MapPin size={18} className="text-secondary" /><h2 className="font-display text-lg font-semibold text-primary">Visitor locations</h2></div>
              {(traffic?.locations || []).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-primary/5"><span className="text-foreground/80">{s.name}</span><span className="font-semibold text-primary">{s.value}</span></div>
              ))}
              {!(traffic?.locations || []).length && <p className="text-sm text-muted-foreground">No data yet.</p>}
            </div>
            <div className="bg-card border border-primary/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><Clock size={18} className="text-secondary" /><h2 className="font-display text-lg font-semibold text-primary">Top pages</h2></div>
              {(traffic?.top_pages || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-primary/5">
                  <span className="text-foreground/80 truncate max-w-[60%]">{p.path}</span>
                  <span className="text-primary"><b>{p.views}</b> <span className="text-muted-foreground text-xs">· {fmtTime(p.avg_dwell)}</span></span>
                </div>
              ))}
              {!(traffic?.top_pages || []).length && <p className="text-sm text-muted-foreground">No data yet.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
